/**
 * Frozen enumerations and caps for the tile schema (H0).
 *
 * ADR-002 §2, §3, §7. These are the closed sets the security model depends on:
 *  - `PROVIDER_IDS` is the **server-enforced allowlist**. A provider not in this
 *    list can never be fetched (SSRF firewall, risk #1). No URL/host ever appears
 *    in a config; the allowlist id maps to a concrete request only server-side in
 *    the adapter's `buildRequest` (ADR-002 §5).
 *  - Every list cap here bounds upstream fan-out (risk #4) and keeps the D-007
 *    cost model honest.
 *
 * Everything is declared `as const` so the values are literal-typed and immutable.
 */

/** Board envelope schema version — the migration unit for grid/layout (ADR-002 §6.1). */
export const BOARD_SCHEMA_VERSION = 1 as const;

/** Per-tile config schema version — evolves independently of the board (ADR-002 §6.1). */
export const TILE_SCHEMA_VERSION = 1 as const;

/**
 * Registered tile types (ADR-002 §3.1). Discriminant for params/variant/glance/tier.
 * Pro-tier and later-phase types are declared now so the discriminated union is
 * stable and old boards referencing them still parse (they are gated at import,
 * not at parse — ADR-002 §7 risk #3).
 */
export const TILE_TYPES = [
  'weather.current',
  'sports.score',
  'markets.quote',
  'news.headlines',
  'clock.local', // no fetch
  'calendar.peek', // Phase 2+ / Pro if OAuth-heavy
  // Local utility widgets (D-015 breadth; clientDirect + noFetch, zero data cost).
  // Their params ARE the storage: user content lives in the board config, edited
  // only via configureTile, computed into display data locally (like clock).
  'note.sticky',
  'todo.list',
  'countdown.event',
  // User-source tiles (D-017 / ADR-005) — the FOURTH tile class. A user-supplied
  // https URL in params, fetched CLIENT-SIDE ONLY (the user's own browser, never
  // our Lambda — same clientDirect discipline as calendar.peek). rss.feed is the
  // flagship: the user pastes a feed URL. clientDirect: true, noFetch: false.
  'rss.feed',
  // Pro / later phases — declared for union stability:
  'markets.realtime',
  'brief.ai',
  'alert.generic',
] as const;

export type TileType = (typeof TILE_TYPES)[number];

/**
 * Server allowlist of upstream providers (ADR-002 §7 risk #1). The **cache proxy
 * is the real gate**; the client Zod enum is defense-in-depth. `local` is the
 * sentinel for no-fetch tiles (e.g. clock) — it never resolves to a network call.
 *
 * Providers are drawn from the ratified data-cost sheet (v1 picks):
 * weather=openweathermap/nws, sports=thesportsdb, markets=finnhub/twelvedata,
 * news=gnews, calendar=google.
 */
export const PROVIDER_IDS = [
  'openweathermap',
  'nws',
  'thesportsdb',
  'finnhub',
  'twelvedata',
  'gnews',
  'google',
  'local', // no-fetch sentinel
  // 'user' is the USER-SOURCE sentinel (D-017 / ADR-005): the "provider" of a
  // user-source tile is the user themselves — the concrete URL lives in the tile's
  // params under `userSourceUrlSchema`, NOT in a server allowlist entry. Like
  // 'local', 'user' never resolves to a server-side buildRequest: user-source tiles
  // are clientDirect and fetched by the browser. It is present here only so the
  // fetch-path provider-allowlist check (tile.ts) admits rss.feed's `provider:'user'`
  // and so cache-key/board serialization can name it. The SSRF firewall is intact:
  // our Lambda still resolves 'user' to nothing — it never fetches a user URL.
  'user',
] as const;

export type ProviderId = (typeof PROVIDER_IDS)[number];

/** Grid tile sizes (ADR-002 §3, glance-grammar-spec §2.1). No fractional sizes. */
export const TILE_SIZES = ['1x1', '2x1', '1x2', '2x2', '3x2'] as const;
export type TileSize = (typeof TILE_SIZES)[number];

/** Capability tier — authoritative value comes from the registry, never the config. */
export const TIERS = ['free', 'pro'] as const;
export type Tier = (typeof TIERS)[number];

/* ---- Fan-out & DoS caps (ADR-002 §7 risk #4, #8) ---- */

/** Max symbols in one markets watchlist (ADR-002 §3.2). */
export const MAX_MARKET_SYMBOLS = 12 as const;

/** Max headlines in one news tile (ADR-002 §3.2 — never an infinite feed). */
export const MAX_NEWS_COUNT = 8 as const;

/* ---- Rung-1: news keyword query (ADR-005 §5) ---- */

/** Min length of an OPTIONAL news keyword query (below this is not a real query). */
export const MIN_NEWS_QUERY_CHARS = 2 as const;

/** Max length of the optional news keyword query (bounds the provider search term). */
export const MAX_NEWS_QUERY_CHARS = 60 as const;

/**
 * Conservative allowlist pattern for a news keyword query (ADR-005 §5). Letters,
 * digits, spaces and a SMALL set of basic punctuation only — deliberately NO
 * quotes, backslashes, or query operators, to keep provider-side query-injection
 * off the table (the GNews search endpoint is the adapter's concern). Matches the
 * empty-safe body via `+`; emptiness is excluded by the length floor.
 */
export const NEWS_QUERY_PATTERN = /^[A-Za-z0-9 .,'&:+\-]+$/;

/* ---- User-source tiles (D-017 / ADR-005) ---- */

/**
 * Max characters in a user-supplied source URL (ADR-005 §2). Kept short: a real
 * feed URL is well under this; the cap bounds the serialized/shared board blob and
 * the client's own fetch target. This is a CONTENT/abuse boundary, not a fan-out
 * limit (user-source tiles are clientDirect — nothing transits our proxy).
 */
export const MAX_USER_SOURCE_URL_CHARS = 300 as const;

/** Max feed items an rss.feed tile may surface (ADR-005 §3 — never an infinite feed). */
export const MAX_RSS_ITEMS = 8 as const;

/** Max events a calendar.peek tile may request (ADR-004; glance shows a handful). */
export const MAX_CALENDAR_EVENTS = 10 as const;

/** Max lookahead window (hours) for a calendar.peek tile (ADR-004; bounds the query). */
export const MAX_CALENDAR_LOOKAHEAD_HOURS = 48 as const;

/* ---- Local-widget content caps (D-015) ----
 * For local utility widgets (note/todo/countdown) the params ARE user CONTENT
 * stored in the board config, not a query. There is NO upstream fetch, so these
 * caps are NOT fan-out limits — they are the ABUSE / SERIALIZED-SIZE boundary:
 * they bound how much text a single tile can push into localStorage and into a
 * shared/cloned board blob (ADR-002 §7 #2 label-safety generalized to content,
 * #4/#8 import-DoS). Content is rendered as plain text, never HTML. */

/** Max characters in a sticky note's body (note.sticky). Empty is allowed. */
export const MAX_NOTE_CHARS = 500 as const;

/** Max characters in a single to-do item's text (todo.list). Must be non-empty. */
export const MAX_TODO_ITEM_CHARS = 100 as const;

/** Max number of items in one to-do list (todo.list). Bounds serialized size. */
export const MAX_TODO_ITEMS = 20 as const;

/** Max characters in a countdown's label (countdown.event). Must be non-empty. */
export const MAX_COUNTDOWN_LABEL_CHARS = 40 as const;

/** Max tiles in one board (bounds import DoS; ADR-002 §7 risk #4/#8). */
export const MAX_TILES_PER_BOARD = 40 as const;

/** Max characters for a user-supplied plain-text tile label (ADR-002 §7 risk #2). */
export const MAX_LABEL_LENGTH = 40 as const;

/**
 * Maximum number of grid rows a board layout may use (H2 bounds clamp).
 * Generous to accommodate long boards; acts as an overflow safety ceiling.
 * Enforced by BoardSchema.superRefine and keyboard-nav clamp.
 */
export const MAX_GRID_ROWS = 64 as const;

/** Tile id format: local handle only, never a capability (ADR-002 §3). */
export const TILE_ID_PATTERN = /^tile_[a-z0-9]{6,12}$/;

/** Anonymous board id format: no PII, safe to share/clone (ADR-002 §6.1). */
export const BOARD_ID_PATTERN = /^board_[a-z0-9]{6,16}$/;

/* ---- Spaces: multiple named boards (D-018 candidate) ---- */

/**
 * Space id format: local handle, same no-PII / not-a-capability discipline as tile
 * and board ids. Mirrors BOARD_ID_PATTERN's shape with a `space_` prefix. The app
 * generates these (like tile ids via generateTileId) and the ops merely validate the
 * pattern via SpacesSchema — ids are never minted inside the schema/ops layer.
 */
export const SPACE_ID_PATTERN = /^space_[a-z0-9]{8,12}$/;

/**
 * Max named spaces one user may keep. Bounds serialized localStorage size and keeps
 * the iOS-style page-dot UX legible (a wall of dots is not glanceable). 8 mirrors a
 * comfortable home-screen page count; raise only with UX evidence.
 */
export const MAX_SPACES = 8 as const;

/**
 * Max characters in a space NAME (e.g. "Morning", "Work", "Markets"). Trimmed plain
 * text, rendered as text never HTML (same label-safety discipline as MAX_LABEL_LENGTH,
 * ADR-002 §7 #2). Short by design — a page label, not a document.
 */
export const MAX_SPACE_NAME_CHARS = 24 as const;

/** Name given to the single board lifted out of the legacy slot on first upgrade. */
export const LEGACY_SPACE_NAME = 'Home' as const;

/** Spaces envelope schema version — its own migration axis (mirrors BOARD_SCHEMA_VERSION). */
export const SPACES_SCHEMA_VERSION = 1 as const;

/** Market ticker symbol format (ADR-002 §3.2). */
export const SYMBOL_PATTERN = /^[A-Z.]{1,8}$/;
